import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Smartphone, Copy, Download, KeyRound } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import { BASE_AUTH_URL } from './constants';

const Spinner = () => (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const primaryButtonClass = 'flex-1 w-full py-3 rounded-xl text-white text-[15px] font-semibold transition hover:brightness-90 active:brightness-75 disabled:opacity-60 disabled:hover:brightness-100 flex items-center justify-center gap-2';
const outlineButtonClass = 'flex-1 w-full py-3 rounded-xl border border-gray-300 text-[15px] font-medium text-gray-800 flex items-center justify-center gap-2 hover:bg-gray-50 transition disabled:opacity-60';

export const DigitCodeInput = ({ value, onChange, length = 6, autoFocus, primaryColor }) => {
  const inputRefs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const setDigit = (index, char) => {
    const next = digits.slice();
    next[index] = char;
    onChange(next.join(''));
  };

  const handleChange = (index, raw) => {
    const char = raw.replace(/\D/g, '').slice(-1);
    setDigit(index, char);
    if (char && index < length - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.padEnd(digits.length, '').slice(0, length));
    inputRefs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className="flex items-center justify-center gap-2.5">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          autoFocus={autoFocus && index === 0}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => { e.target.style.borderColor = primaryColor || '#6D28D9'; }}
          onBlur={(e) => { e.target.style.borderColor = '#E5E7EB'; }}
          style={{ borderColor: '#E5E7EB' }}
          className="w-12 h-14 border rounded-lg text-center text-[20px] font-semibold outline-none transition-colors"
        />
      ))}
    </div>
  );
};

const compactOutlineClass = 'flex-1 px-4 py-2 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors flex items-center justify-center gap-2';

export const RecoveryCodesPanel = ({ codes, primaryColor, onDone, compact = false }) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const downloadCodes = () => {
    const blob = new Blob([`${t('auth.mfaRecoveryFileHeader')}\n\n${codes.join('\n')}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'docai-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {!compact && (
        <h2 className="text-[22px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaRecoveryTitle')}</h2>
      )}
      <p className={compact ? 'text-[14px] text-[#6B7280] mb-4' : 'text-gray-500 text-center text-[14px] mb-5 leading-relaxed'}>{t('auth.mfaRecoveryText')}</p>
      <div className={`grid grid-cols-2 gap-2 bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-4 ${compact ? 'rounded-lg' : 'rounded-xl'}`}>
        {codes.map((code) => (
          <span key={code} className="font-mono text-[14px] text-gray-900 text-center">{code}</span>
        ))}
      </div>
      <div className="flex gap-2.5 mb-4">
        <button type="button" onClick={copyCodes} className={compact ? compactOutlineClass : outlineButtonClass}>
          <Copy size={16} />
          {copied ? t('auth.mfaRecoveryCopied') : t('auth.mfaRecoveryCopy')}
        </button>
        <button type="button" onClick={downloadCodes} className={compact ? compactOutlineClass : outlineButtonClass}>
          <Download size={16} />
          {t('auth.mfaRecoveryDownload')}
        </button>
      </div>
      <label className={`flex items-center gap-2 cursor-pointer select-none ${compact ? '' : 'mb-5'}`}>
        <input
          type="checkbox"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
          style={{ accentColor: primaryColor }}
        />
        <span className="text-[13px] text-gray-600">{t('auth.mfaRecoverySavedCheckbox')}</span>
      </label>
      {compact ? (
        <div className="flex items-center justify-end gap-3 -mx-6 -mb-5 mt-5 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          <button
            type="button"
            disabled={!saved}
            onClick={onDone}
            className="px-6 py-2.5 text-[14px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors min-w-[100px] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: primaryColor }}
          >
            {t('auth.mfaRecoveryDone')}
          </button>
        </div>
      ) : (
        <button type="button" disabled={!saved} onClick={onDone} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
          {t('auth.mfaRecoveryDone')}
        </button>
      )}
    </div>
  );
};

const STEPS = ['install', 'scan', 'confirm', 'recovery'];

const MfaEnrollment = ({ authorization, primaryColor, onComplete, onCancel }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('install');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [issuer, setIssuer] = useState('');
  const [showManualKey, setShowManualKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const startedRef = useRef(false);

  const handleError = (err) => setError(err?.response?.data?.detail || t('auth.genericError'));

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setLoading(true);
    axios.post(`${BASE_AUTH_URL}/mfa/setup`, {}, { headers: { Authorization: authorization } })
      .then((res) => {
        setQrCode(res.data.qr_code);
        setSecret(res.data.secret);
        setIssuer(res.data.issuer);
      })
      .catch(handleError)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (next) => { setError(''); setStep(next); };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_AUTH_URL}/mfa/confirm`, { code }, { headers: { Authorization: authorization } });
      setResult(res.data);
      goTo('recovery');
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const stepIndex = STEPS.indexOf(step) + 1;

  return (
    <div>
      <p className="text-[12px] font-medium text-center text-gray-400 mb-4">
        {t('auth.mfaEnrollStep', { current: stepIndex, total: STEPS.length })}
      </p>

      {error && (
        <div className="mb-5 text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</div>
      )}

      {step === 'install' && (
        <div>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}1A`, color: primaryColor }}>
              <Smartphone size={22} />
            </div>
          </div>
          <h2 className="text-[22px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaInstallTitle')}</h2>
          <p className="text-gray-500 text-center text-[14px] mb-7 leading-relaxed">{t('auth.mfaInstallText')}</p>
          <div className="flex gap-2.5">
            {onCancel && (
              <button type="button" onClick={onCancel} className={outlineButtonClass}>{t('common.cancel')}</button>
            )}
            <button type="button" disabled={loading || !qrCode} onClick={() => goTo('scan')} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('common.continue')}
            </button>
          </div>
        </div>
      )}

      {step === 'scan' && (
        <div>
          <h2 className="text-[22px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaScanTitle')}</h2>
          <p className="text-gray-500 text-center text-[14px] mb-5 leading-relaxed">{t('auth.mfaScanText', { issuer })}</p>
          {qrCode && (
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <img src={qrCode} alt="QR" className="w-40 h-40" />
              </div>
            </div>
          )}
          {!showManualKey ? (
            <button type="button" onClick={() => setShowManualKey(true)}
              className="w-full mb-5 flex items-center justify-center gap-1.5 text-[13px] font-medium transition hover:opacity-75" style={{ color: primaryColor }}>
              <KeyRound size={15} />
              {t('auth.mfaManualKeyButton')}
            </button>
          ) : (
            <div className="mb-5">
              <p className="text-[12px] text-gray-500 text-center mb-2">{t('auth.mfaManualKeyText')}</p>
              <button type="button" onClick={copyKey}
                className="w-full flex items-center justify-center gap-1.5 text-[12px] font-mono font-medium text-gray-800 bg-white border border-gray-200 rounded-full px-4 py-3 whitespace-nowrap overflow-hidden transition hover:bg-gray-50">
                <span className="truncate">{keyCopied ? t('auth.mfaRecoveryCopied') : (secret.match(/.{1,4}/g) || [secret]).join(' ')}</span>
                <span className="shrink-0" style={{ color: primaryColor }}><Copy size={14} /></span>
              </button>
            </div>
          )}
          <div className="flex gap-2.5">
            <button type="button" onClick={() => goTo('install')} className={outlineButtonClass}>{t('common.back')}</button>
            <button type="button" onClick={() => goTo('confirm')} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
              {t('common.continue')}
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <form onSubmit={confirmCode}>
          <h2 className="text-[22px] font-semibold text-center mb-2 tracking-tight text-gray-900">{t('auth.mfaConfirmTitle')}</h2>
          <p className="text-gray-500 text-center text-[14px] mb-6 leading-relaxed">{t('auth.mfaConfirmText')}</p>
          <DigitCodeInput value={code} onChange={setCode} autoFocus primaryColor={primaryColor} />
          <div className="flex gap-2.5 mt-6">
            <button type="button" onClick={() => goTo('scan')} className={outlineButtonClass}>{t('common.back')}</button>
            <button type="submit" disabled={loading || code.length !== 6} className={primaryButtonClass} style={{ backgroundColor: primaryColor }}>
              {loading ? <Spinner /> : t('common.confirm')}
            </button>
          </div>
        </form>
      )}

      {step === 'recovery' && result && (
        <RecoveryCodesPanel codes={result.recovery_codes || []} primaryColor={primaryColor} onDone={() => onComplete(result)} />
      )}
    </div>
  );
};

export default MfaEnrollment;
